// Copyright (c) Docugami, Inc. All rights reserved.

import { IPage, Shimmer, Stack } from "@fluentui/react";
import { UnassignedDocSetId } from "components/file_management/file_helpers";
import { DocumentCommandBar } from "components/file_management/presentation/DocumentCommandBar";
import { IDocumentPageViewItem, ListViewColumns } from "components/file_management/presentation/DocumentListView";
import { documentToViewItem, DocumentViewer, DocumentViewerProps, getSortableDocumentFieldFromViewItemField, IDocumentViewControls } from "components/file_management/presentation/DocumentViewer";
import { getDocumentStatusInfo } from "filemanager/FileStatus";
import { useAssignDocSetCommand } from "hooks/AssignDocSetCommand";
import { useAutoAssignCommand } from "hooks/AutoAssignCommand";
import { useDownloadFilesCommand } from "hooks/DownloadFilesCommand";
import { useFilePreview } from "hooks/FilePreview";
import { useGetDocumentsFilters } from "hooks/GetDocumentsFilters";
import { useUnassignedFilesCoachmarks } from "hooks/UnassignedFilesCoachmarks";
import { useVirtualizedDocsetDocuments } from "hooks/VirtualizedDocsetDocuments";
import { useEffect, useMemo, useRef, useState } from "react";
import { getProviderIdFromPath } from "storage/StorageProviderHelpers";
import styled from "styled-components";
import { useDeleteFilesCommand } from "../../hooks/DeleteFilesCommand";
import { DocsetFiltersCommandBar } from "./DocsetFilterCommandBar";
import { DocumentCountInfo } from "./DocumentCountInfo";
import { FilterIconButton } from "./ExplorerFilterIconButton";
import { InfoItemFooter } from "./ExplorerStyles";
import { ReviewDocsId } from "./ExplorerTypes";
import { ExplorerView } from "./ExplorerView";
import { SelectionCountButton } from "./SelectionCountButton";

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
`;

const AbsoluteContainer = styled.div`
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
`;

const defaultPageSize = 10;
export const UnassignedExplorerView = (): JSX.Element => {
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [showFilters, setShowFilters] = useState(false);
    const [pageOffsets, setPageOffsets] = useState<number[]>([0]);

    const viewRef = useRef<IDocumentViewControls>(null);
    const columnFilter: ListViewColumns = ListViewColumns.Icon | ListViewColumns.Name | ListViewColumns.DocStatus | ListViewColumns.DocSource | ListViewColumns.AddedOn | ListViewColumns.Size;

    const filtersProps = useGetDocumentsFilters();
    const { setSortState } = filtersProps;
    const { isLoading, totalCount, items } = useVirtualizedDocsetDocuments(UnassignedDocSetId, defaultPageSize, filtersProps.filters, pageOffsets);
    const unassignedCount = totalCount ?? 0;

    const documentViewItems: IDocumentPageViewItem[] = useMemo(() =>
        items.map(document => !!document ? documentToViewItem(document, getDocumentStatusInfo(document), undefined, getProviderIdFromPath(document.fileId)) : document)
        , [items])

    const documentIds = useMemo(() => items.filter(x => !!x).map(x => x!.folderId), [items]);
    const { filePreview, setPreviewFile, canPreview, previewCommands } = useFilePreview(documentIds);

    useEffect(() => {
        if (!filePreview) {
            viewRef.current?.selectItems(selectedItems);
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [filePreview]);

    // Commands
    const deleteFilesCommand = useDeleteFilesCommand(selectedItems, () => { });
    const reassignCommand = useAssignDocSetCommand("Assign to", selectedItems, () => { }, selectedItems.length === unassignedCount);
    const autoAssignCommand = useAutoAssignCommand(selectedItems, unassignedCount, () => { });
    const downloadCommand = useDownloadFilesCommand(selectedItems, () => { });

    const commands = useMemo(() => [autoAssignCommand, reassignCommand, downloadCommand, deleteFilesCommand], [autoAssignCommand, reassignCommand, downloadCommand, deleteFilesCommand]);
    const coachMark = useUnassignedFilesCoachmarks(unassignedCount);

    const overlays = useMemo(() => {
        const overlays = commands.filter(c => c.isActive && !!c.visual).map(x => x.visual);
        overlays.push(coachMark);
        return overlays;
    }, [commands, coachMark]);

    const documentViewProps: DocumentViewerProps = {
        items: documentViewItems,
        isLoading: isLoading,
        selectedItems: selectedItems,
        disableSelectAll: true,
        filter: columnFilter,
        onSelectedItemsChanged: items => setSelectedItems(items),
        actions: {
            name: item => setPreviewFile(item.folderId)
        },
        validateActions: {
            name: item => canPreview(item.folderId)
        },
        sortableColumns: ['name'],
        onSort: (field, descending) => {
            const sortField = getSortableDocumentFieldFromViewItemField(field ?? '');
            setSortState(!!sortField ? { field: sortField, descending: descending } : undefined)
        },
        listProps: {
            onPageAdded: (page: IPage) => {
                const offset = page.startIndex;
                if (pageOffsets.includes(offset)) {
                    return;
                }

                setPageOffsets(x => [...x, offset]);
            },
            onPageRemoved: (page: IPage) => {
                const offset = page.startIndex;
                if (!pageOffsets.includes(offset) || offset === 0) {
                    return;
                }

                setPageOffsets(x => x.filter(x => x !== offset));
            },
            getPageSpecification: () => ({ itemCount: defaultPageSize })
        }
    };

    const header = (
        <Stack horizontal verticalAlign="center">
            <Stack.Item styles={{ root: { display: 'flex' } }}>
                <FilterIconButton disabled={!!filePreview || totalCount === 0} onClick={() => setShowFilters(v => !v)} />
                <SelectionCountButton
                    hidden={!selectedItems.length || !!filePreview}
                    count={selectedItems.length}
                    onClear={() => viewRef.current?.clearSelection()} />
            </Stack.Item>
            <DocumentCommandBar commands={!!filePreview ? previewCommands : commands} disable={!!coachMark} />
        </Stack>
    )
    const content = (
        <ContentContainer>
            <DocsetFiltersCommandBar hidden={!showFilters} onDismiss={() => setShowFilters(false)} {...filtersProps} />
            {!!filePreview ? <AbsoluteContainer>{filePreview}</AbsoluteContainer> : <DocumentViewer ref={viewRef} {...documentViewProps} />}
        </ContentContainer>
    )
    const footer = (
        <InfoItemFooter>
            <Shimmer isDataLoaded={!isLoading} width={"50px"}>
                {totalCount !== undefined && <DocumentCountInfo count={totalCount} countName={'Doc'} />}
            </Shimmer>
        </InfoItemFooter>
    )

    return (
        <ExplorerView
            header={header}
            content={content}
            overlays={!!filePreview ? previewCommands.map(x => x.visual) : overlays}
            footer={!!filePreview ? null : footer}
            telemetryId={ReviewDocsId.Unassigned} />
    );
}
